import React from 'react';

/**
 * Animated radar SVG — a sweep beam rotates over concentric rings,
 * with small dots that pulse as the beam passes. Pure CSS animation,
 * no JS runtime cost.
 */
const RadarAnimation: React.FC<{
  size?: number;
  className?: string;
}> = ({ size = 32, className = '' }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 48 48"
    className={className}
    aria-hidden
  >
    {/* Concentric rings */}
    <circle cx="24" cy="24" r="20" fill="none" stroke="currentColor" className="text-slate-200" strokeWidth="0.8" />
    <circle cx="24" cy="24" r="14" fill="none" stroke="currentColor" className="text-slate-200" strokeWidth="0.6" />
    <circle cx="24" cy="24" r="8" fill="none" stroke="currentColor" className="text-slate-200" strokeWidth="0.5" />

    {/* Crosshair lines */}
    <line x1="24" y1="4" x2="24" y2="44" stroke="currentColor" className="text-slate-150" strokeWidth="0.4" opacity="0.4" />
    <line x1="4" y1="24" x2="44" y2="24" stroke="currentColor" className="text-slate-150" strokeWidth="0.4" opacity="0.4" />

    {/* Sweep beam: bright leading arm at 12 o'clock, afterglow trails BEHIND
        it (counter-clockwise toward 9 o'clock) since the group spins clockwise. */}
    <g style={{ transformOrigin: '24px 24px', animation: 'radar-spin 4.5s linear infinite' }}>
      <path
        d="M24 24 L24 4 A20 20 0 0 0 4 24 Z"
        fill="url(#radarGrad)"
        opacity="0.55"
      />
      {/* Leading edge highlight */}
      <line x1="24" y1="24" x2="24" y2="4" stroke="#ec4899" strokeWidth="1" opacity="0.7" />
    </g>

    {/* Blip dots — fixed positions, fade in as the sweep beam passes (no
        movement/scaling so they read as "tracked targets", not jitter).
        Delays match each dot's clock angle so they light in sync with the beam. */}
    <circle cx="31" cy="13" r="1.6" fill="#ec4899" style={{ animation: 'radar-blip 4.5s linear infinite backwards', animationDelay: '0.41s' }} />
    <circle cx="34" cy="28" r="1.6" fill="#ec4899" style={{ animation: 'radar-blip 4.5s linear infinite backwards', animationDelay: '1.4s' }} />
    <circle cx="14" cy="28" r="1.6" fill="#ec4899" style={{ animation: 'radar-blip 4.5s linear infinite backwards', animationDelay: '3.1s' }} />

    {/* Center dot */}
    <circle cx="24" cy="24" r="2" fill="#ec4899" />

    <defs>
      <linearGradient id="radarGrad" x1="24" y1="4" x2="4" y2="24" gradientUnits="userSpaceOnUse">
        <stop offset="0%" stopColor="#ec4899" stopOpacity="0.55" />
        <stop offset="100%" stopColor="#ec4899" stopOpacity="0" />
      </linearGradient>
    </defs>

    <style>{`
      @keyframes radar-spin {
        from { transform: rotate(0deg); }
        to { transform: rotate(360deg); }
      }
      @keyframes radar-blip {
        0% { opacity: 0; }
        3% { opacity: 1; }
        22% { opacity: 0.5; }
        45% { opacity: 0; }
        100% { opacity: 0; }
      }
    `}</style>
  </svg>
);

export default RadarAnimation;
